import axios from 'axios';

export const DEFAULT_SYSTEM_CONFIGURATION = Object.freeze({
  theme_mode: 'light', layout_type: 'vertical', app_name: 'AAI LES', system_logo: null,
  company_name: 'Company', support_email: '', hotline: '', timezone: 'Asia/Singapore',
  date_format: 'YYYY-MM-DD', maintenance_mode: false,
  footer_copyright_text: '{year} © {appName}.', footer_credit_text: 'Design & Develop by {companyName}',
  preset_color: 'default', primary_color: '#405189', button_radius: 'rounded', button_weight: '600',
  button_hover_effect: 'lift', button_label_style: 'left', input_radius: 'rounded', input_border_width: '1px', input_style: 'default',
  input_focus_ring: 'primary', checkbox_radius: 'subtle', checkbox_size: 'default', checkbox_border_width: '1.5px',
  layout_config: { login_banner: { status: 'All support systems operational', title: 'One service desk.', highlight: 'Every team moving', description: 'Request the help you need.' } },
});

const endpoint = `${process.env.VUE_APP_AUTHDOMAIN}/api/v1/system-configuration`;
const isHex = (value) => /^#[0-9A-Fa-f]{6}$/.test(value || '');
const rgb = (hex) => `${parseInt(hex.slice(1, 3), 16)}, ${parseInt(hex.slice(3, 5), 16)}, ${parseInt(hex.slice(5, 7), 16)}`;
const map = (value, values, fallback) => values.includes(value) ? value : fallback;

export function normalizeSystemConfiguration(configuration = {}) {
  const merged = { ...DEFAULT_SYSTEM_CONFIGURATION, ...configuration };
  return {
    ...merged,
    primary_color: isHex(merged.primary_color) ? merged.primary_color.toUpperCase() : DEFAULT_SYSTEM_CONFIGURATION.primary_color,
    button_radius: map(merged.button_radius, ['square', 'subtle', 'rounded', 'pill'], 'rounded'),
    button_weight: map(String(merged.button_weight), ['400', '500', '600', '700'], '600'),
    button_hover_effect: map(merged.button_hover_effect, ['none', 'lift', 'glow'], 'lift'),
    button_label_style: map(merged.button_label_style, ['standard', 'left', 'right', 'pill'], 'left'),
    input_radius: map(merged.input_radius, ['square', 'subtle', 'rounded', 'pill'], 'rounded'),
    input_border_width: map(merged.input_border_width, ['1px', '2px'], '1px'),
    input_style: map(merged.input_style, ['default', 'subtle'], 'default'),
    input_focus_ring: map(merged.input_focus_ring, ['primary', 'subtle', 'minimal'], 'primary'),
    checkbox_radius: map(merged.checkbox_radius, ['square', 'subtle', 'circle'], 'subtle'),
    checkbox_size: map(merged.checkbox_size, ['compact', 'default', 'large'], 'default'),
    checkbox_border_width: map(merged.checkbox_border_width, ['1px', '1.5px', '2px'], '1.5px'),
    layout_config: {
      ...DEFAULT_SYSTEM_CONFIGURATION.layout_config,
      ...(merged.layout_config || {}),
      login_banner: {
        ...DEFAULT_SYSTEM_CONFIGURATION.layout_config.login_banner,
        ...(merged.layout_config?.login_banner || {}),
      },
    },
  };
}

export function applySystemConfiguration(configuration) {
  const config = normalizeSystemConfiguration(configuration);
  const root = document.documentElement;
  const radius = { square: '0px', subtle: '4px', rounded: '8px', pill: '50px' };
  const checkRadius = { square: '0px', subtle: '4px', circle: '50%' };
  const checkSize = { compact: '15px', default: '18px', large: '22px' };
  const ring = config.input_focus_ring === 'minimal' ? 'transparent' : config.input_focus_ring === 'subtle' ? `rgba(${rgb(config.primary_color)}, .12)` : `rgba(${rgb(config.primary_color)}, .24)`;
  root.style.setProperty('--app-primary', config.primary_color);
  root.style.setProperty('--app-primary-rgb', rgb(config.primary_color));
  root.style.setProperty('--vz-primary', config.primary_color);
  root.style.setProperty('--vz-primary-rgb', rgb(config.primary_color));
  root.style.setProperty('--app-btn-radius', radius[config.button_radius]);
  root.style.setProperty('--app-btn-font-weight', config.button_weight);
  root.style.setProperty('--app-btn-hover-transform', config.button_hover_effect === 'lift' ? 'translateY(-2px)' : 'none');
  root.style.setProperty('--app-btn-hover-shadow', config.button_hover_effect === 'glow' ? `0 6px 18px rgba(${rgb(config.primary_color)}, .35)` : config.button_hover_effect === 'lift' ? '0 5px 12px rgba(0, 0, 0, .14)' : 'none');
  root.style.setProperty('--app-input-radius', radius[config.input_radius] === '50px' ? '20px' : radius[config.input_radius]);
  root.style.setProperty('--app-input-border-width', config.input_border_width);
  root.style.setProperty('--app-input-bg', config.input_style === 'subtle' ? `rgba(${rgb(config.primary_color)}, .06)` : 'var(--vz-form-surface)');
  root.style.setProperty('--app-input-focus-ring', ring);
  root.style.setProperty('--app-check-radius', checkRadius[config.checkbox_radius]);
  root.style.setProperty('--app-check-size', checkSize[config.checkbox_size]);
  root.style.setProperty('--app-check-border-width', config.checkbox_border_width);
  localStorage.setItem('systemConfiguration', JSON.stringify(config));
  window.dispatchEvent(new CustomEvent('app:system-configuration-updated', { detail: config }));
  return config;
}

export async function getSystemConfiguration() {
  const response = await axios.get(endpoint);
  return applySystemConfiguration(response.data?.data);
}

export async function updateSystemConfiguration(configuration) {
  const response = await axios.put(endpoint, configuration);
  return applySystemConfiguration(response.data?.data);
}

export function applyCachedSystemConfiguration() {
  try { return applySystemConfiguration(JSON.parse(localStorage.getItem('systemConfiguration') || '{}')); } catch { return applySystemConfiguration(DEFAULT_SYSTEM_CONFIGURATION); }
}
